import React, { useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { Check, Clock, Gavel, Lock, LockOpen, TriangleAlert } from "lucide-react";
import {
    METHOD_LABEL,
    ROUNDS,
    boutVerdict,
    isMmaPair,
    isStoppage,
    judgeCell,
    judgeSuggestion,
    lockedRoundsOf,
} from "../utils/judge-scoring";
import { unlockJudgeRound } from "../utils/firebase";

const UNLOCK_REASON_MAX = 300;

const shortName = (name) => {
    const [first, second] = String(name || "").split(/\s+/);
    return second ? `${first} ${second[0]}.` : first || "?";
};

/**
 * The judges' cards for one bout on Manage Event's Matched tab: one cell per
 * judge and, once there is one, a suggested result.
 *
 * ACCEPT only fills in the bout's existing WIN / DRAW and TKO / KO /
 * SUBMISSION / DECISION choices, exactly as clicking them would (a judge's
 * SUB is the admin's SUBMISSION). Nothing is saved until the admin
 * goes through the page's usual End Event / update-records flow.
 *
 * Judges score RED and BLUE corners; the card stores which fighter each
 * corner was, so picks are shown by fighter name and in the card's A / B
 * (left / right) order regardless of corner.
 *
 * @param {object}   props
 * @param {Array}    props.judges        every judge on the event
 * @param {object}   props.cardsByJudge  this bout's cards, keyed by judgeId
 * @param {object}   props.fighterA      left = red event-fighter (with .fighter),
 *                                       after resolveCorners / any corner swap
 * @param {object}   props.fighterB      right = blue event-fighter
 * @param {object}   props.matchedFighter  the bout's current result selections
 * @param {boolean}  [props.readOnly]
 * @param {Function} props.onAccept      ({ isDraw, winnerId, loserId, winBy })
 * @param {string}   [props.eventId]     with pairKey, enables round Unlock
 * @param {string}   [props.pairKey]
 *
 * Unlock: a judge's round locks when they press NEXT ROUND. An admin can
 * unlock one round per judge here, with a reason (unlockJudgeRound). The
 * judge re-scores it and presses NEXT ROUND again; a submitted card reopens
 * and needs the judge's PIN again. The partner sees the change as a revision.
 */
export default function BoutJudgeStrip({
    judges,
    cardsByJudge,
    fighterA,
    fighterB,
    matchedFighter,
    readOnly = false,
    onAccept,
    eventId,
    pairKey,
}) {
    const [unlocking, setUnlocking] = useState(null); // { judge, card }
    const [unlockRound, setUnlockRound] = useState(null);
    const [unlockReason, setUnlockReason] = useState("");
    const [unlockBusy, setUnlockBusy] = useState(false);

    if (!judges?.length) return null;
    const canUnlock = !readOnly && !!eventId && !!pairKey;

    const openUnlock = (judge, card) => {
        const locked = lockedRoundsOf(card);
        // Preselect the only locked round, or the latest one.
        const last = locked.lastIndexOf(true);
        setUnlocking({ judge, card });
        setUnlockRound(last >= 0 ? last + 1 : null);
        setUnlockReason("");
    };
    const closeUnlock = () => {
        if (unlockBusy) return;
        setUnlocking(null);
    };
    const confirmUnlock = async (e) => {
        e?.preventDefault();
        const reason = unlockReason.trim();
        if (!unlocking || !unlockRound || !reason || unlockBusy) return;
        setUnlockBusy(true);
        try {
            const res = await unlockJudgeRound({
                eventId,
                boutKey: pairKey,
                judgeId: unlocking.judge.id,
                round: unlockRound,
                reason,
            });
            if (res?.success) {
                toast.success(
                    res.reopened
                        ? `Round ${unlockRound} unlocked. The card is reopened; ${unlocking.judge.name} re-submits with their PIN.`
                        : `Round ${unlockRound} unlocked for ${unlocking.judge.name}.`
                );
                setUnlocking(null);
            } else {
                toast.error(res?.error || "Couldn't unlock the round.");
            }
        } catch (error) {
            console.error("unlockJudgeRound failed:", error);
            toast.error("Couldn't unlock the round. Try again.");
        } finally {
            setUnlockBusy(false);
        }
    };

    const aId = fighterA?.fighterId;
    const bId = fighterB?.fighterId;
    const nameOf = (id) =>
        shortName(id === aId ? fighterA?.fighter?.name : id === bId ? fighterB?.fighter?.name : "");

    // Any card tells us which fighter was in which corner.
    const anyCard = Object.values(cardsByJudge ?? {})[0];
    const cornerId = (side) =>
        side === "red" ? anyCard?.redFighterId ?? aId : anyCard?.blueFighterId ?? bId;
    const cornerOf = (fighterId) =>
        fighterId === cornerId("red") ? "red" : fighterId === cornerId("blue") ? "blue" : null;

    // A SUB card on a match that isn't MMA (an entry changed after it went
    // in) is a conflict: the admin decides.
    const isMma = isMmaPair(fighterA, fighterB);
    const suggestion = judgeSuggestion(judges, cardsByJudge, aId, bId, matchedFighter, { isMma });
    const verdict = suggestion?.verdict ?? boutVerdict(judges, cardsByJudge, { isMma });

    const scoreLine = (card) => {
        // Totals in the bout card's left–right order.
        const red = card.total.red;
        const blue = card.total.blue;
        return cornerOf(aId) === "blue" ? `${blue}–${red}` : `${red}–${blue}`;
    };

    return (
        <div className="w-full flex flex-col gap-2 py-1">
            <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${Math.min(judges.length, 4)}, minmax(0, 1fr))` }}
            >
                {judges.map((judge) => {
                    const cell = judgeCell(judge, cardsByJudge?.[judge.id]);
                    const pickId =
                        cell.pick === "red" || cell.pick === "blue" ? cornerId(cell.pick) : null;
                    const stoppage = cell.state === "in" && isStoppage(cell.card?.method);
                    const lockedCount = cell.card
                        ? lockedRoundsOf(cell.card).filter((l, i) => l && cell.card.rounds?.[i]).length
                        : 0;
                    return (
                        <div
                            key={judge.id}
                            className={twMerge(
                                "border p-2 text-center min-w-0 bg-base-100",
                                cell.state === "revoked" && "opacity-50"
                            )}
                        >
                            <p className="text-[0.6875rem] font-bold tracking-widest opacity-60 truncate uppercase">
                                {judge.name}
                            </p>
                            <p
                                className={twMerge(
                                    "text-xl font-bold tabular-nums leading-tight",
                                    cell.state !== "in" && "opacity-40"
                                )}
                            >
                                {cell.scored ? scoreLine(cell) : "—"}
                            </p>
                            {cell.state === "in" ? (
                                <span
                                    className={twMerge(
                                        "inline-block mt-0.5 px-1.5 py-0.5 text-[0.625rem] font-extrabold tracking-wider text-white max-w-full truncate",
                                        cell.pick === "red"
                                            ? "bg-red-600"
                                            : cell.pick === "blue"
                                            ? "bg-blue-600"
                                            : "bg-base-300"
                                    )}
                                >
                                    {stoppage
                                        ? `${cell.card.method} R${cell.card.endAt ?? ROUNDS} · `
                                        : ""}
                                    {pickId ? nameOf(pickId).toUpperCase() : "EVEN"}
                                </span>
                            ) : (
                                <span className="badge badge-ghost badge-sm mt-1">
                                    {cell.state === "revoked"
                                        ? "REVOKED"
                                        : cell.state === "scoring"
                                        ? `SCORING ${cell.scored}/${ROUNDS}`
                                        : "NOT IN"}
                                </span>
                            )}
                            {lockedCount > 0 && (
                                // Wraps in a narrow cell (phones): UNLOCK goes under the count.
                                <div className="flex flex-wrap items-center justify-center gap-x-1 mt-1 text-[0.625rem] font-bold tracking-wider opacity-70">
                                    <span className="flex items-center gap-1 whitespace-nowrap">
                                        <Lock size={10} />
                                        {lockedCount}/{ROUNDS} LOCKED
                                    </span>
                                    {canUnlock && cell.state !== "revoked" && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs h-5 min-h-5 px-1 gap-0.5 whitespace-nowrap"
                                            title="Unlock a round so the judge can correct it"
                                            onClick={() => openUnlock(judge, cell.card)}
                                        >
                                            <LockOpen size={11} />
                                            UNLOCK
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap min-h-8">
                {verdict.status === "waiting" || verdict.status === "none" ? (
                    <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide opacity-60">
                        <Clock size={18} />
                        Waiting for all cards · {verdict.in}/{verdict.total} in
                    </span>
                ) : verdict.status === "conflict" ? (
                    <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-warning">
                        <TriangleAlert size={18} className="flex-none" />
                        {verdict.reason === "sub_not_mma"
                            ? "A judge scored a submission, but this isn't an MMA match · decide from the referee's call"
                            : "Judges disagree on how it ended · decide from the referee's call"}
                    </span>
                ) : (
                    <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white min-w-0">
                        <Gavel size={18} className="flex-none" />
                        <span className="truncate">
                            {verdict.status === "stoppage" ? METHOD_LABEL[verdict.kind] ?? verdict.kind : verdict.kind}
                            {" · "}
                            {suggestion.isDraw ? "Draw" : nameOf(suggestion.winnerId)}
                        </span>
                        <small className="font-semibold opacity-60 tracking-wider">
                            {verdict.status === "stoppage"
                                ? `R${verdict.rounds.join("/")} · ${verdict.in}/${verdict.total} cards`
                                : verdict.line}
                        </small>
                    </span>
                )}
                {suggestion && !readOnly && (
                    <button
                        type="button"
                        className={twMerge("btn btn-sm", !suggestion.applied && "btn-primary")}
                        disabled={suggestion.applied}
                        title="Fills in the result below. Nothing is saved until you end the event."
                        onClick={() =>
                            onAccept?.({
                                isDraw: suggestion.isDraw,
                                winnerId: suggestion.winnerId,
                                loserId: suggestion.loserId,
                                winBy: suggestion.winBy,
                            })
                        }
                    >
                        {suggestion.applied ? (
                            <>
                                <Check size={16} />
                                ACCEPTED
                            </>
                        ) : (
                            "ACCEPT"
                        )}
                    </button>
                )}
            </div>

            {unlocking && (
                <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="unlock-round-title">
                    <form className="modal-box border flex flex-col gap-4" onSubmit={confirmUnlock}>
                        <div>
                            <h3 id="unlock-round-title" className="font-bold text-lg">
                                Unlock a round · {unlocking.judge.name}
                            </h3>
                            <p className="text-sm opacity-70 mt-1">
                                The judge can change the round, then presses NEXT ROUND to lock it again.
                                Until then the partner API shows the round as not scored; afterwards the
                                change is published as a revision.
                            </p>
                            {unlocking.card?.submitted && (
                                <p className="text-sm text-warning mt-2">
                                    This card is submitted. Unlocking reopens it, and the judge must submit
                                    again with their PIN.
                                </p>
                            )}
                        </div>
                        <div className="flex gap-1" role="radiogroup" aria-label="Round">
                            {Array.from({ length: ROUNDS }, (_, i) => i + 1).map((n) => {
                                const locked = lockedRoundsOf(unlocking.card)[n - 1];
                                const score = unlocking.card?.rounds?.[n - 1];
                                return (
                                    <button
                                        key={n}
                                        type="button"
                                        role="radio"
                                        aria-checked={unlockRound === n}
                                        disabled={!locked || unlockBusy}
                                        onClick={() => setUnlockRound(n)}
                                        className={twMerge(
                                            "btn flex-1 h-12 min-h-12 px-0 flex-col gap-0.5",
                                            unlockRound === n && "btn-primary"
                                        )}
                                    >
                                        <b className="text-base leading-none flex items-center gap-0.5">
                                            {n}
                                            {locked && <Lock size={10} />}
                                        </b>
                                        <small className="text-[0.625rem] font-medium opacity-70 leading-none">
                                            {score ? `${score.red}–${score.blue}` : "—"}
                                        </small>
                                    </button>
                                );
                            })}
                        </div>
                        <label className="form-control w-full">
                            <span className="label-text font-semibold mb-1">Reason (required)</span>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                rows={2}
                                maxLength={UNLOCK_REASON_MAX}
                                placeholder="e.g. Judge tapped the wrong corner"
                                value={unlockReason}
                                onChange={(e) => setUnlockReason(e.target.value)}
                                disabled={unlockBusy}
                                autoFocus
                            />
                        </label>
                        <div className="flex gap-2">
                            <button type="button" className="btn flex-1" onClick={closeUnlock} disabled={unlockBusy}>
                                CANCEL
                            </button>
                            <button
                                type="submit"
                                className="btn btn-warning flex-1"
                                disabled={!unlockRound || !unlockReason.trim() || unlockBusy}
                            >
                                {unlockBusy ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <>
                                        <LockOpen size={16} />
                                        UNLOCK ROUND {unlockRound ?? ""}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                    <button
                        type="button"
                        className="modal-backdrop"
                        aria-hidden="true"
                        tabIndex={-1}
                        onClick={closeUnlock}
                    />
                </div>
            )}
        </div>
    );
}
